/**
 * Admin hook for viewing email connections across all companies.
 * Used by admin dashboard to monitor white-glove onboarding status.
 */

import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection, query, getDocs, collectionGroup, orderBy, limit } from 'firebase/firestore';
import { db, functions } from '../firebaseConfig';

export interface EmailConnectionAdmin {
  connectionId: string;
  companyId: string;
  companyName?: string;
  type: 'gmail' | 'outlook';
  userEmail: string;
  userId: string;
  status:
    | 'pending_auth'
    | 'auth_exchanging'
    | 'active'
    | 'paused'
    | 'error'
    | 'revoked'
    | 'pending_invite'
    | 'reauth_required'
    | 'admin_consent_required'
    | 'auth_failed';
  lastSyncAt?: Date;
  lastSyncResult?: 'success' | 'partial' | 'failed';
  lastSyncError?: string;
  /** Files the last sync skipped as already loaded (not an error). */
  lastSyncDuplicatesSkipped?: number;
  /** Reason the last OAuth exchange or refresh failed (Microsoft path). */
  lastAuthError?: string;
  /** Microsoft directory (tenant) id, present on Outlook connections. */
  providerTenantId?: string;
  messagesProcessed: number;
  filesExtracted: number;
  rctiStored: number;
  rctiPaired: number;
  createdAt: Date;
}

export interface SyncResult {
  connectionId: string;
  userEmail: string;
  status: 'success' | 'partial' | 'failed';
  messagesScanned: number;
  filesExtracted: number;
  filesSkippedDuplicate: number;
  filesSkippedNoMatch: number;
  errors: string[];
}

interface UseEmailConnectionsAdminReturn {
  connections: EmailConnectionAdmin[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  triggerSync: (companyId: string) => Promise<SyncResult[]>;
  isSyncing: boolean;
  stats: {
    totalConnections: number;
    activeConnections: number;
    totalFilesExtracted: number;
    companiesWithConnections: number;
  };
}

/**
 * Admin hook to view and manage email connections across all companies.
 */
export function useEmailConnectionsAdmin(): UseEmailConnectionsAdminReturn {
  const [connections, setConnections] = useState<EmailConnectionAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);

  // Fetch all connections across companies
  const fetchConnections = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);

      // Query all email_connections using collection group query
      const connectionsQuery = query(
        collectionGroup(db, 'email_connections'),
        orderBy('createdAt', 'desc'),
        limit(100)
      );

      const snapshot = await getDocs(connectionsQuery);

      // Get company names for display
      const companyIds = new Set<string>();
      const connectionsData: EmailConnectionAdmin[] = [];

      for (const doc of snapshot.docs) {
        const data = doc.data();
        const companyId = doc.ref.parent.parent?.id || '';
        companyIds.add(companyId);

        connectionsData.push({
          connectionId: data.connectionId || doc.id,
          companyId,
          type: data.type,
          userEmail: data.userEmail || 'Pending...',
          userId: data.userId,
          status: data.status,
          lastSyncAt: data.lastSyncAt?.toDate?.(),
          lastSyncResult: data.lastSyncResult,
          lastSyncError: data.lastSyncError,
          lastSyncDuplicatesSkipped: data.lastSyncDuplicatesSkipped || 0,
          lastAuthError: data.lastAuthError,
          providerTenantId: data.providerTenantId,
          messagesProcessed: data.messagesProcessed || 0,
          filesExtracted: data.filesExtracted || 0,
          rctiStored: data.rctiStored || 0,
          rctiPaired: data.rctiPaired || 0,
          createdAt: data.createdAt?.toDate?.() || new Date(),
        });
      }

      // Fetch company names
      const companyNames: Record<string, string> = {};
      for (const companyId of Array.from(companyIds)) {
        try {
          const companyQuery = query(collection(db, 'companies'));
          const companySnapshot = await getDocs(companyQuery);
          companySnapshot.docs.forEach(companyDoc => {
            if (companyDoc.id === companyId) {
              companyNames[companyId] = companyDoc.data().companyName || companyDoc.data().name || companyId;
            }
          });
        } catch (e) {
          // Ignore errors fetching company names
        }
      }

      // Add company names to connections
      const connectionsWithNames = connectionsData.map(conn => ({
        ...conn,
        companyName: companyNames[conn.companyId] || conn.companyId,
      }));

      setConnections(connectionsWithNames);
    } catch (err: any) {
      console.error('Failed to fetch email connections:', err);
      setError(err.message || 'Failed to load email connections');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  // Trigger manual sync for a company
  const triggerSync = useCallback(async (companyId: string): Promise<SyncResult[]> => {
    setIsSyncing(true);
    try {
      const triggerEmailSync = httpsCallable<{ companyId: string }, SyncResult[]>(
        functions,
        'triggerEmailSync'
      );

      const result = await triggerEmailSync({ companyId });

      // Refetch to update stats
      await fetchConnections();

      return result.data;
    } finally {
      setIsSyncing(false);
    }
  }, [fetchConnections]);

  // Calculate stats
  const stats = {
    totalConnections: connections.length,
    activeConnections: connections.filter(c => c.status === 'active').length,
    totalFilesExtracted: connections.reduce((sum, c) => sum + c.filesExtracted, 0),
    companiesWithConnections: new Set(connections.map(c => c.companyId)).size,
  };

  return {
    connections,
    loading,
    error,
    refetch: fetchConnections,
    triggerSync,
    isSyncing,
    stats,
  };
}
